import { noop } from "lodash-es";
import React, {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useStoreServiceApi } from "@api/hooks/storeService.hook";

import { isDefined, isEmpty } from "@utils/helpers";
import { useWoosmapServices } from "@utils/hooks/useWoosmapServices.hook";

import { LocationService } from "@services/location.service";

import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { StoresMapper } from "@redux/modules/api/data/stores/stores.mapper";
import { broadcastMessage } from "@redux/modules/value/messages/messages.utils";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import {
    SearchItem,
    SearchItemType,
} from "../StoreSearchInput/storeSearchInput.types";
import {
    STORE_PICKER_MESSAGE_ID,
    STORE_PICKER_STORES_LIMIT,
} from "./storePicker.config";
import { StorePickerConfig } from "./storePicker.container";
import { StorePickerStore } from "./types/storePickerStore.types";
import { StorePickerType } from "./types/storePickerType.enum";

type StorePickerContextValue = {
    // null -> no search has been executed yet (no results is not visible)
    stores: Array<StorePickerStore> | null;
    loading: boolean;
    config: StorePickerConfig;
    searchItem: SearchItem | undefined;
    selectedStore: StoreDetailsModel | null;
    activeFeatures: Array<string>;
    activeFilters: Array<string>;
    setSearchItem: (searchItem: SearchItem | undefined) => void;
    setSelectedStore: (code: StorePickerStore | null) => void;
    toggleFeature: (feature: string) => void;
    toggleFilter: (filter: string) => void;
    submit: () => void;
    clear: () => void;
};

type StorePickerContextProviderProps = PropsWithChildren<{
    disabled: boolean;
    sku?: string;
    config: StorePickerConfig;
    onSubmit: (store: StoreDetailsModel) => void;
}>;

export const StorePickerContext = createContext<
    StorePickerContextValue | undefined
>(undefined);
StorePickerContext.displayName = "StorePickerContext";

export const StorePickerContextProvider = ({
    disabled,
    sku,
    config,
    onSubmit,
    children,
}: StorePickerContextProviderProps) => {
    const { t } = useGranite();

    const [selectedStore, setSelectedStore] =
        useState<StoreDetailsModel | null>(null);

    const [searchItem, setSearchItem] = useState<SearchItem | undefined>(
        undefined,
    );

    const [activeFeatures, setActiveFeatures] = useState<Array<string>>([]);

    const [activeFilters, setActiveFilters] = useState<Array<string>>([]);

    const { DistanceService } = useWoosmapServices();

    const [loadingLocation, setLoadingLocation] = useState(false);

    const [{ data, loading }, updateStores] = useStoreServiceApi(
        {
            url: "/localized/stores",
            method: "GET",
            mapper: StoresMapper,
        },
        {
            manual: true,
            useCache: true,
        },
    );

    const handleOnLocalityNotFoundError = useCallback(
        () =>
            broadcastMessage({
                id: STORE_PICKER_MESSAGE_ID,
                text: t("storesearch.storedetails.notFound"),
                status: "danger",
                dismissible: true,
            }),
        [t],
    );

    const setCurrentLocationAsSerachItem = useCallback(() => {
        setLoadingLocation(true);

        LocationService.getCurrentPosition()
            .then(({ latitude, longitude }) => {
                setSearchItem({
                    value: "",
                    latitude: latitude,
                    longitude: longitude,
                    label: t("form.storePicker.myLocation"),
                    type: SearchItemType.Locality,
                });
            })
            .catch(handleOnLocalityNotFoundError)
            .finally(() => setLoadingLocation(false));
    }, [t, setLoadingLocation, setSearchItem, handleOnLocalityNotFoundError]);

    const stores = useMemo(() => {
        if (!isDefined(data) || loadingLocation || loading) return null;

        return config.type === StorePickerType.CUSTOM
            ? data.filter(store => config.storeCodes.includes(store.code))
            : data;
    }, [data, searchItem, DistanceService, config]);

    const selectedFeatures = useMemo(
        () => [...activeFeatures, ...(config.fixedFeatures ?? [])],
        [activeFeatures, config.fixedFeatures],
    );

    const selectedFilters = useMemo(
        () => [...activeFilters, ...(config.fixedFilters ?? [])],
        [activeFilters, config.fixedFilters],
    );

    useEffect(() => {
        const abortController = new AbortController();

        if (disabled) {
            return;
        } else if (
            !isDefined(searchItem) &&
            !isDefined(config.storeCode) &&
            isEmpty(activeFeatures) &&
            isEmpty(activeFilters)
        ) {
            // Ignore initial render
            return;
        } else if (!isDefined(searchItem) && !isDefined(config.storeCode)) {
            // The user selected a filter or feature without providing a search item
            // we set the search item to the current location
            setCurrentLocationAsSerachItem();
            return;
        }

        const limitSearchParams =
            config.type === StorePickerType.CUSTOM
                ? undefined
                : { limit: STORE_PICKER_STORES_LIMIT.toString() };

        const skuSearchParams = !!sku ? { sku: sku } : undefined;

        const includeProductAvailabilityParams = {
            includeProductAvailability: (
                config.displayStockLevel && !!sku
            ).toString(),
        };

        const featuresSearchParams = !isEmpty(selectedFeatures)
            ? {
                  features: selectedFeatures.join(","),
              }
            : undefined;

        const filtersSearchParams = selectedFilters.reduce<
            Record<string, string>
        >(
            (acc, filter) => ({
                ...acc,
                [filter]: true.toString(),
            }),
            {},
        );

        const storeIdSearchParams =
            (isDefined(searchItem) && searchItem.type === SearchItemType.Store
                ? { storeId: searchItem.value }
                : undefined) ??
            (isDefined(config.storeCode) &&
            searchItem?.type !== SearchItemType.Locality
                ? { storeId: config.storeCode }
                : undefined);

        const coordinatesSearchParams =
            searchItem?.type === SearchItemType.Locality &&
            isDefined(searchItem.latitude) &&
            isDefined(searchItem.longitude)
                ? {
                      latitude: searchItem.latitude.toString(),
                      longitude: searchItem.longitude.toString(),
                  }
                : undefined;

        updateStores({
            signal: abortController.signal,
            params: {
                filterInactive: true.toString(),
                ...limitSearchParams,
                ...includeProductAvailabilityParams,
                ...skuSearchParams,
                ...featuresSearchParams,
                ...filtersSearchParams,
                ...storeIdSearchParams,
                ...coordinatesSearchParams,
            },
        })
            .then(stores => {
                // Select store based on selected store in autocomplete
                if (
                    isDefined(searchItem) &&
                    searchItem.type === SearchItemType.Store
                ) {
                    const store = stores.data.find(
                        store => store.code === searchItem.value,
                    );

                    setSelectedStore(store ?? null);
                }

                // Select initial store when opening the picker
                else if (config.type === StorePickerType.SELECT) {
                    const store = stores.data.find(
                        store => store.code === config.storeCode,
                    );

                    setSelectedStore(store ?? null);
                }

                // Clear selection because this store is not in the list anymore
                else {
                    setSelectedStore(null);
                }

                return stores;
            })
            .catch(noop);

        return () => abortController.abort();
    }, [
        searchItem,
        selectedFeatures,
        activeFilters,
        config,
        disabled,
        setCurrentLocationAsSerachItem,
        DistanceService,
    ]);

    const submit = useCallback(() => {
        if (!isDefined(selectedStore)) return;
        onSubmit(selectedStore);
    }, [selectedStore, onSubmit]);

    const clear = useCallback(() => {
        setSearchItem(undefined);
        setActiveFeatures([]);
        setActiveFilters([]);
    }, [setSearchItem, setActiveFeatures, setActiveFilters]);

    const toggleFeature = (feature: string) =>
        setActiveFeatures(currentFeatures =>
            currentFeatures.includes(feature)
                ? currentFeatures.filter(f => f !== feature)
                : [...currentFeatures, feature],
        );

    const toggleFilter = (filter: string) =>
        setActiveFilters(currentFilters =>
            currentFilters.includes(filter)
                ? currentFilters.filter(f => f !== filter)
                : [...currentFilters, filter],
        );

    return (
        <StorePickerContext.Provider
            value={{
                config: config,
                stores: stores,
                loading: loading || loadingLocation,
                searchItem: searchItem,
                selectedStore: selectedStore,
                activeFeatures: activeFeatures,
                activeFilters: activeFilters,
                setSearchItem: setSearchItem,
                setSelectedStore: setSelectedStore,
                toggleFeature: toggleFeature,
                toggleFilter: toggleFilter,
                submit: submit,
                clear: clear,
            }}
        >
            {children}
        </StorePickerContext.Provider>
    );
};

export const useStorePickerContext = () => {
    const context = useContext<StorePickerContextValue | undefined>(
        StorePickerContext,
    );

    if (!isDefined(context)) {
        throw new Error(`${StorePickerContext.displayName} is undefined.`);
    }

    return context;
};
